/**
 * src/pages/reports/_printPrep.js — make the report printable. Card P225.
 *
 * ⛔⛔ THE DEFECT, AND THE MEASUREMENT THAT NAMED IT. Daniel printed the live
 * report on v2.1.61 and the graphs were missing. Four causes were plausible;
 * the card asked which, so it was measured with --print-to-pdf against the
 * REAL modal chain rather than reasoned about:
 *
 *   · (a) ResponsiveContainer measuring 0x0  — RULED OUT. Every chart SVG
 *         measured 664x240 (and 664x200/220) with the print class applied,
 *         with matching width/height ATTRIBUTES. Nothing was zero.
 *   · (c) charts portalled outside .ceo-ai-report — RULED OUT. All ten SVGs
 *         reported insideReport=true.
 *   · (d) lazy mounting — RULED OUT. All seven charts existed before print.
 *   · (b) CLIPPING — CONFIRMED, and it is the whole cause.
 *
 * ⚠⚠ AND THE SYMPTOM WAS NARROWER THAN REPORTED. It is not that the graphs
 * do not print: the printed document is TRUNCATED TO ONE PAGE. Section 1 and
 * its chart print perfectly; sections 2-7, their six charts, the risks and
 * the actions are simply not there. A reader seeing one page with one chart
 * reasonably concludes "the charts are missing", which is why the reported
 * symptom pointed at recharts and the cause is three divs above it.
 *
 * THE ANCESTOR CHAIN, measured (DrilldownModal.js:160, :176, :217):
 *     div[position:fixed, overflow:auto]      <- the backdrop
 *       div[max-height:calc(100vh - 80px)]    <- the dialog
 *         div[overflow:auto]                  <- the scroll body
 *           section.ceo-ai-report             <- P201 makes this position:absolute
 *
 * A position:fixed ancestor is painted ONCE in paged media and sized to the
 * viewport, and P201's `position: absolute` on the report resolves against
 * exactly that box — so the report is laid out inside a one-viewport frame
 * that two further ancestors also clip with overflow and max-height.
 *
 * ⛔ WHY THIS IS JAVASCRIPT AND NOT MORE CSS. The three clipping ancestors
 * are styled INLINE by DrilldownModal and carry no classes, so no stylesheet
 * can reach them. Adding classes to that component would couple the report's
 * print path to a shared modal used by every drill-down on the dashboard;
 * walking up from the report and neutralising what clips touches only the
 * ancestors this report actually has, and restores them afterwards.
 */

/** Properties that can clip or trap the document in one page. */
const CLIPPING = ['overflow', 'overflowX', 'overflowY', 'maxHeight', 'height', 'position'];

/** The size charts are pinned to for print — a Letter column at 96dpi. */
export const PRINT_CHART = Object.freeze({ width: 680, height: 260 });

/*
 * ⚠⚠ AN EMPTY STRING MEANS "NOT SET", NOT "SET TO SOMETHING UNUSUAL". A
 * browser's getComputedStyle returns 'none'/'visible' for unset values;
 * jsdom returns ''. The first version compared against 'none' and 'visible'
 * alone, so EVERY ancestor looked like it was clipping — a plain padding div
 * was rewritten too, which an arm caught. Treating '' as unset keeps the pass
 * to the elements that really do clip.
 */
const unset = (v) => v === '' || v === undefined || v === null;

function isClipping(cs) {
  const clips = (v) => !unset(v) && v !== 'visible';
  return cs.position === 'fixed'
    || clips(cs.overflow) || clips(cs.overflowY) || clips(cs.overflowX)
    || (!unset(cs.maxHeight) && cs.maxHeight !== 'none');
}

/**
 * Prepare the document for printing. Returns a restore function.
 *
 * ⚠ IDEMPOTENT AND SELF-RESTORING. beforeprint can fire more than once (a
 * preview reopened, Cmd-P twice), and leaving a dialog permanently unclipped
 * would turn the CEO dashboard into one endlessly scrolling page.
 */
export function prepareForPrint(rootEl, doc) {
  const d = doc || (typeof document !== 'undefined' ? document : null);
  const root = rootEl || (d && d.querySelector('.ceo-ai-report'));
  if (!d || !root || typeof window === 'undefined') return () => {};

  const undo = [];

  /* ── 1 · Un-clip every ancestor between the report and <body>. ────────── */
  let cursor = root.parentElement;
  let depth = 0;
  while (cursor && cursor !== d.body && depth < 20) {
    /*
     * ⛔⛔ CAPTURED PER ITERATION. The first version closed over the loop
     * variable itself, which `while` reassigns — so every restore function
     * ended up pointing at the LAST element the walk touched, and restoring
     * wrote the backdrop's saved styles onto whatever it finished on. An arm
     * caught it: position came back 'static' instead of 'fixed', which would
     * have left the dashboard's modal permanently unpositioned after one
     * print. A for..of over a collected array would do as well; `const el`
     * inside the body is the smallest fix.
     */
    const el = cursor;
    const cs = window.getComputedStyle(el);
    if (isClipping(cs)) {
      const saved = {};
      for (const prop of CLIPPING) saved[prop] = el.style[prop];
      undo.push(() => { for (const prop of CLIPPING) el.style[prop] = saved[prop] || ''; });
      el.style.overflow = 'visible';
      el.style.overflowX = 'visible';
      el.style.overflowY = 'visible';
      el.style.maxHeight = 'none';
      el.style.height = 'auto';
      /* ⛔ fixed -> static, not relative: `relative` would still make this a
         containing block for the report's absolute positioning, which is the
         half of the problem P201 introduced. */
      if (cs.position === 'fixed') el.style.position = 'static';
    }
    cursor = cursor.parentElement;
    depth += 1;
  }

  /* ── 2 · Pin every chart to a fixed print size. ───────────────────────── */
  /*
   * ⚠ DEFENSIVE, NOT CORRECTIVE. The measurement above found no zero-sized
   * chart, so this is not what fixes Daniel's report — the un-clipping is.
   * It is here because recharts sizes from a measured box and a print layout
   * fires no resize, so a narrower window or a different engine can produce
   * one; and because a fixed size makes the printed output the same width
   * every time rather than whatever the reader's window happened to be.
   */
  const wells = root.querySelectorAll('.ceo-chart-well, [data-print-section]');
  wells.forEach((well) => {
    const svg = well.querySelector('svg');
    if (!svg) return;
    const targets = [well, ...well.querySelectorAll('.recharts-wrapper, .recharts-responsive-container')];
    for (const t of targets) {
      const saved = { width: t.style.width, height: t.style.height, maxHeight: t.style.maxHeight };
      undo.push(() => { t.style.width = saved.width || ''; t.style.height = saved.height || '';
                        t.style.maxHeight = saved.maxHeight || ''; });
      t.style.width = `${PRINT_CHART.width}px`;
      t.style.height = `${PRINT_CHART.height}px`;
      t.style.maxHeight = 'none';
    }
    const savedSvg = { w: svg.getAttribute('width'), h: svg.getAttribute('height') };
    undo.push(() => {
      if (savedSvg.w === null) svg.removeAttribute('width'); else svg.setAttribute('width', savedSvg.w);
      if (savedSvg.h === null) svg.removeAttribute('height'); else svg.setAttribute('height', savedSvg.h);
    });
    svg.setAttribute('width', String(PRINT_CHART.width));
    svg.setAttribute('height', String(PRINT_CHART.height));
  });

  let restored = false;
  return function restore() {
    if (restored) return;
    restored = true;
    /* Reverse order: the chart sizes were pushed after the ancestors. */
    for (let i = undo.length - 1; i >= 0; i--) {
      try { undo[i](); } catch (_) { /* one failed restore must not strand the rest */ }
    }
  };
}

/**
 * Wire prepare/restore to the browser's own print lifecycle.
 *
 * ⚠ BOTH EVENTS AND THE BUTTON. The Print button calls prepareForPrint
 * directly because window.print() is synchronous and must not race the
 * event; these listeners cover every OTHER way a print starts — Cmd-P, the
 * File menu, a print preview reopened — which is how Daniel printed.
 */
export function installPrintHooks(getRoot) {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return () => {};
  }
  let restore = null;
  const before = () => {
    if (restore) restore();
    restore = prepareForPrint(typeof getRoot === 'function' ? getRoot() : null);
  };
  const after = () => { if (restore) { restore(); restore = null; } };

  window.addEventListener('beforeprint', before);
  window.addEventListener('afterprint', after);
  return () => {
    window.removeEventListener('beforeprint', before);
    window.removeEventListener('afterprint', after);
    if (restore) { restore(); restore = null; }
  };
}
